export type TimezoneOption = {
    value: string
    label: string
}

export type TimezoneGroup = {
    label: string
    options: TimezoneOption[]
}

export const TIMEZONE_GROUPS: TimezoneGroup[] = [
    {
        label: 'Americas',
        options: [
            { value: 'US/Hawaii', label: '(GMT-10:00) Hawaii' },
            { value: 'US/Alaska', label: '(GMT-09:00) Alaska' },
            { value: 'US/Pacific', label: '(GMT-08:00) Pacific Time (US & Canada)' },
            { value: 'US/Arizona', label: '(GMT-07:00) Arizona' },
            { value: 'US/Mountain', label: '(GMT-07:00) Mountain Time (US & Canada)' },
            { value: 'America/Mexico_City', label: '(GMT-06:00) Mexico City' },
            { value: 'US/Central', label: '(GMT-06:00) Central Time (US & Canada)' },
            { value: 'US/Eastern', label: '(GMT-05:00) Eastern Time (US & Canada)' },
            { value: 'America/Buenos_Aires', label: '(GMT-03:00) Buenos Aires' },
        ],
    },
    {
        label: 'Europe',
        options: [
            { value: 'Europe/Dublin', label: '(GMT) Dublin' },
            { value: 'Europe/London', label: '(GMT) London' },
            { value: 'Europe/Amsterdam', label: '(GMT+01:00) Amsterdam' },
            { value: 'Europe/Berlin', label: '(GMT+01:00) Berlin' },
            { value: 'Europe/Brussels', label: '(GMT+01:00) Brussels' },
            { value: 'Europe/Madrid', label: '(GMT+01:00) Madrid' },
            { value: 'Europe/Paris', label: '(GMT+01:00) Paris' },
            { value: 'Europe/Rome', label: '(GMT+01:00) Rome' },
            { value: 'Europe/Stockholm', label: '(GMT+01:00) Stockholm' },
            { value: 'Europe/Istanbul', label: '(GMT+02:00) Istanbul' },
        ],
    },
    {
        label: 'Asia',
        options: [
            { value: 'Asia/Riyadh', label: '(GMT+03:00) Riyadh' },
            { value: 'Asia/Dubai', label: '(GMT+04:00) Dubai' },
            { value: 'Asia/Karachi', label: '(GMT+05:00) Karachi' },
            { value: 'Asia/Kolkata', label: '(GMT+05:30) Kolkata' },
            { value: 'Asia/Dhaka', label: '(GMT+06:00) Dhaka' },
            { value: 'Asia/Bangkok', label: '(GMT+07:00) Bangkok' },
            { value: 'Asia/Jakarta', label: '(GMT+07:00) Jakarta' },
            { value: 'Asia/Hong_Kong', label: '(GMT+08:00) Hong Kong' },
            { value: 'Asia/Kuala_Lumpur', label: '(GMT+08:00) Kuala Lumpur' },
            { value: 'Asia/Singapore', label: '(GMT+08:00) Singapore' },
            { value: 'Asia/Seoul', label: '(GMT+09:00) Seoul' },
            { value: 'Asia/Tokyo', label: '(GMT+09:00) Tokyo' },
        ],
    },
    {
        label: 'Oceania',
        options: [
            { value: 'Australia/Perth', label: '(GMT+08:00) Perth' },
            { value: 'Australia/Melbourne', label: '(GMT+10:00) Melbourne' },
            { value: 'Australia/Sydney', label: '(GMT+10:00) Sydney' },
            { value: 'Pacific/Auckland', label: '(GMT+12:00) Auckland' },
        ],
    },
]

/** Flat list — same order as groups (for callers that don't need headers). */
export const TIMEZONES: TimezoneOption[] = TIMEZONE_GROUPS.flatMap((group) => group.options)

/** Browser / stored IANA ids → values we expose in the dropdown. */
const TIMEZONE_ALIASES: Record<string, string> = {
    'Pacific/Honolulu': 'US/Hawaii',
    'America/Anchorage': 'US/Alaska',
    'America/Los_Angeles': 'US/Pacific',
    'America/Phoenix': 'US/Arizona',
    'America/Denver': 'US/Mountain',
    'America/Chicago': 'US/Central',
    'America/New_York': 'US/Eastern',
    'America/Toronto': 'US/Eastern',
    'America/Argentina/Buenos_Aires': 'America/Buenos_Aires',
}

export function getDetectedLocalTimezone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata'
    } catch {
        return 'Asia/Kolkata'
    }
}

function formatIanaLabel(tzid: string): string {
    return tzid.replace(/_/g, ' ')
}

/**
 * Maps a browser/stored timezone id to a dropdown `value`.
 * Ensures Select `value` always matches a MenuItem (or falls back to the raw id for "Other").
 */
export function resolveToDropdownTimezone(tz?: string | null): string {
    const candidate = tz || getDetectedLocalTimezone()
    const mapped = TIMEZONE_ALIASES[candidate] || candidate
    const known = TIMEZONES.find((option) => option.value === mapped || option.value === candidate)
    return known?.value ?? candidate
}

export function getTimezoneLabel(tz: string, opts?: { jobCountry?: string | null }): string {
    const local = getLocalTimezoneOption()
    if (tz === local.value) return local.label

    const jobOption = getTimezoneOptionForCountry(opts?.jobCountry)
    if (jobOption && tz === jobOption.value) return jobOption.label

    return TIMEZONES.find((option) => option.value === tz)?.label || formatIanaLabel(tz)
}

/**
 * Primary timezone for a job-posting country (only when we have a matching dropdown entry).
 * Multi-zone countries use a common business default (e.g. US → Eastern).
 */
const COUNTRY_PRIMARY_TIMEZONE: Record<string, string> = {
    India: 'Asia/Kolkata',
    Singapore: 'Asia/Singapore',
    United_States: 'US/Eastern',
    'United States': 'US/Eastern',
    United_Kingdom: 'Europe/London',
    'United Kingdom': 'Europe/London',
    United_Arab_Emirates: 'Asia/Dubai',
    'United Arab Emirates': 'Asia/Dubai',
    Malaysia: 'Asia/Kuala_Lumpur',
    Thailand: 'Asia/Bangkok',
    Australia: 'Australia/Sydney',
    Germany: 'Europe/Berlin',
    France: 'Europe/Paris',
    Japan: 'Asia/Tokyo',
    South_Korea: 'Asia/Seoul',
    'South Korea': 'Asia/Seoul',
    Indonesia: 'Asia/Jakarta',
    Hong_Kong_SAR_China: 'Asia/Hong_Kong',
    'Hong Kong SAR China': 'Asia/Hong_Kong',
    Hong_Kong: 'Asia/Hong_Kong',
    'Hong Kong': 'Asia/Hong_Kong',
    Ireland: 'Europe/Dublin',
    Netherlands: 'Europe/Amsterdam',
    Belgium: 'Europe/Brussels',
    Spain: 'Europe/Madrid',
    Italy: 'Europe/Rome',
    Sweden: 'Europe/Stockholm',
    Turkey: 'Europe/Istanbul',
    Saudi_Arabia: 'Asia/Riyadh',
    'Saudi Arabia': 'Asia/Riyadh',
    Pakistan: 'Asia/Karachi',
    Bangladesh: 'Asia/Dhaka',
    New_Zealand: 'Pacific/Auckland',
    'New Zealand': 'Pacific/Auckland',
    Mexico: 'America/Mexico_City',
    Argentina: 'America/Buenos_Aires',
    Canada: 'US/Eastern',
}

/** Pinned job-country timezone option (also still listed under its continent group). */
export function getTimezoneOptionForCountry(country?: string | null): TimezoneOption | null {
    if (!country?.trim()) return null

    const trimmed = country.trim()
    const tzValue = COUNTRY_PRIMARY_TIMEZONE[trimmed] || COUNTRY_PRIMARY_TIMEZONE[trimmed.replace(/\s+/g, '_')]
    if (!tzValue) return null

    const known = TIMEZONES.find((tz) => tz.value === tzValue)
    if (!known) return null

    return {
        value: known.value,
        label: `${known.label}`,
    }
}

/** Local timezone option pinned at the top of selects (still listed again in its continent group). */
export function getLocalTimezoneOption(): TimezoneOption {
    const resolved = resolveToDropdownTimezone(getDetectedLocalTimezone())
    const known = TIMEZONES.find((tz) => tz.value === resolved)

    if (known) {
        return {
            value: known.value,
            label: `${known.label}`,
        }
    }

    return {
        value: resolved,
        label: `${formatIanaLabel(resolved)}`,
    }
}

export default TIMEZONES
